import { useState, CSSProperties } from "react";
import { useStore } from "reactflow";
import { useAuthStore } from "../../../store/storeAuth";
import { alpha } from "@mui/material";
import {
    UseConditionHandleProps,
    DEFAULT_ACTIVE_COLOR,
    INVALID_SOURCE_HANDLES,
    DASHED_BORDER,
} from "./types.d";

export const useConditionHandle = ({
    isActive = false,
    activeColor,
    type,
}: UseConditionHandleProps) => {
    const user = useAuthStore((state) => state.user);
    const [isHovered, setIsHovered] = useState(false);

    const isDraggingConnection = useStore((s) => s.connectionNodeId !== null);
    const sourceHandleId = useStore((s) => s.connectionStartHandle?.handleId);
    const sourceHandleType = useStore((s) => s.connectionStartHandle?.type);

    const isSameType = sourceHandleType === type;
    const isInvalidSource =
        !!sourceHandleId &&
        INVALID_SOURCE_HANDLES.some(handle => sourceHandleId?.startsWith(handle));

    const isDark = user?.theme === "dark";
    const isDropTarget =
        isDraggingConnection && isHovered && !isInvalidSource && !isSameType;

    const resolvedColor = activeColor || DEFAULT_ACTIVE_COLOR;

    const backgroundColor = isDropTarget
        ? resolvedColor
        : isActive
            ? isDark ? "#1D2939" : "#E7F0FF"
            : isDark ? "#344054" : "#EAECF0";

    const HANDLE_SIZE = 18;
    const HALF_SIZE = HANDLE_SIZE / 2;

    const baseStyle: CSSProperties = {
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: `${HANDLE_SIZE}px`,
        height: `${HANDLE_SIZE}px`,
        background: "transparent",
        border: "none",
        transform: `translateY(-${HALF_SIZE}px)`,
        ...(isDraggingConnection && (isInvalidSource || isSameType) && {
            cursor: "not-allowed",
        }),
    };

    const visualStyle: CSSProperties = {
        boxSizing: "border-box",
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor,
        backgroundImage: isActive || isDropTarget ? "inherit" : DASHED_BORDER,
        border: isActive
            ? `1px solid ${resolvedColor}`
            : undefined,
        boxShadow: isDropTarget
            ? `0 0 0 4px ${alpha(resolvedColor, 0.25)}, 0 0 12px ${alpha(resolvedColor, 1)}`
            : undefined,
        transition:
            "background-color 100ms ease, border-color 100ms ease, box-shadow 100ms ease, transform 100ms ease",
        transform: `scale(${isDropTarget ? "1.15" : "1"})`,
        transformOrigin: "center center",
        pointerEvents: "none",
    };

    const handleMouseEnter = () => {
        if (isDraggingConnection && !isSameType) setIsHovered(true);
    };

    const handleMouseLeave = () => setIsHovered(false);

    return {
        isDropTarget,
        baseStyle,
        visualStyle,
        handleMouseEnter,
        handleMouseLeave,
    };
};
